import cx from "classnames";
import { HTMLProps } from "react";
import { match, P } from "ts-pattern";

import { Enum_Componententriessmartphoneentry_Category } from "graphql.d";
import { useFunnelContext } from "shared/Funnel/FunnelContextProvider";
import { useProductPageContext } from "shared/product/ProductPageContext/useProductPageContext";
import { Button } from "ui/Button";
import { Loader } from "ui/Loader";
import { PushableButtonLink } from "ui/PushableButtonLink";
import { formatToCurrency } from "utils/price.utils";
import { useModalState } from "utils/useModalState";
import { usePriceAdvantage } from "utils/usePriceAdvantage";
import { useI18n } from "utils/with-i18n.utils";

import { ProductOrderButton } from "../../ProductOrderButton";
import { WithProductReminderModal } from "../WithProductReminderModal";

export const StickySection = ({
  className,
}: {
  className?: HTMLProps<HTMLDivElement>["className"];
}) => {
  const i18n = useI18n();

  const { isOpen, closeModal, openModal } = useModalState();
  const { family, currentVariant, subscriptionDropSkus } =
    useProductPageContext();
  const { isSwap } = useFunnelContext();

  const isComingSoon =
    family?.category ===
    Enum_Componententriessmartphoneentry_Category.ComingSoon;

  const isOutOfCatalogueWithReplacement =
    family?.category === Enum_Componententriessmartphoneentry_Category.Legacy &&
    family.replacement;

  const replacementModelSlug = `/smartphone${family?.replacement?.modelSlug}`;
  const replacementVariant = family?.replacement?.availableCatalogueEntry;
  const originalSubscriptionPrice =
    (replacementVariant?.originalSubscriptionPrice ??
      currentVariant?.originalSubscriptionPrice) ||
    0;
  const subscriptionPrice =
    (replacementVariant?.minimumSubscriptionPrice ??
      currentVariant?.subscriptionPrice) ||
    0;
  const originalUpfrontPrice =
    (replacementVariant?.originalUpfrontPrice ??
      currentVariant?.originalUpfrontPrice) ||
    0;
  const upfrontPrice =
    (replacementVariant?.minimumUpfrontPrice ?? currentVariant?.upfrontPrice) ||
    0;

  const {
    discountInfo,
    smartphoneFamilyHasSubscriptionPriceDrop,
    smartphoneFamilyHasUpfrontPriceDrop,
    hasSeniorityDiscount,
  } = usePriceAdvantage({
    originalUpfrontPrice,
    upfrontPrice,
    originalSubscriptionPrice,
    subscriptionPrice,
  });

  if (isSwap && !discountInfo) {
    return <Loader />;
  }

  // If isOutOfCatalogueWithReplacement is true, we return true here
  // so that currentVariantHasSubscriptionPromotion will depend solely
  // on smartphoneFamilyHasSubscriptionPriceDrop
  const isCurrentVariantSkuInDropList = isOutOfCatalogueWithReplacement
    ? true
    : subscriptionDropSkus?.includes(currentVariant?.sku || "");

  // check if the sku of the currentVariant is part of the subscriptionDropSkus
  // i.e., currentVariant's subscription has formerSubscriptionPriceInCentsIncludingTax
  const currentVariantHasSubscriptionPromotion =
    smartphoneFamilyHasSubscriptionPriceDrop && isCurrentVariantSkuInDropList;

  const formattedSubscriptionPrice = formatToCurrency(
    "fr-FR",
    "EUR",
    subscriptionPrice,
    undefined,
    false,
  );

  const subscriptionPriceLabelOptions = currentVariantHasSubscriptionPromotion
    ? {
        subscriptionPrice: formattedSubscriptionPrice,
        subscriptionPriceClassName:
          "text-2xl font-bold leading-none items-center text-[#7D89FF]",
      }
    : {
        subscriptionPrice: formattedSubscriptionPrice,
        subscriptionPriceClassName:
          "text-2xl font-bold leading-none items-center",
      };

  const subscriptionPriceLabel = i18n.t(
    "product.stickySection.subscriptionPriceDiscount",
    subscriptionPriceLabelOptions,
    { html: true },
  );

  const currentVariantHasUpfrontPromotion =
    smartphoneFamilyHasUpfrontPriceDrop && isCurrentVariantSkuInDropList;

  const formattedOriginalPriceUpfront = formatToCurrency(
    "fr-FR",
    "EUR",
    originalUpfrontPrice,
    undefined,
    true,
  );
  const formattedPriceUpfront = hasSeniorityDiscount
    ? 0
    : formatToCurrency("fr-FR", "EUR", upfrontPrice, undefined, true);

  const discountLabelOptions = match({
    hasSeniorityDiscount,
    currentVariantHasUpfrontPromotion,
    currentVariantHasSubscriptionPromotion,
  })
    .with({ hasSeniorityDiscount: true }, () => ({}))
    .with(
      {
        hasSeniorityDiscount: P.union(false, P.nullish),
        currentVariantHasUpfrontPromotion: true,
      },
      () => ({
        upfrontPriceDifference:
          ((currentVariant?.originalUpfrontPrice ?? 0) -
            (currentVariant?.upfrontPrice ?? 0)) /
          100,
      }),
    )
    .with(
      {
        hasSeniorityDiscount: P.union(false, P.nullish),
        currentVariantHasUpfrontPromotion: P.union(false, P.nullish),
        currentVariantHasSubscriptionPromotion: true,
      },
      () => ({}),
    )
    .otherwise(() => null);

  const discountLabelKey = match({
    hasSeniorityDiscount,
    currentVariantHasUpfrontPromotion,
    currentVariantHasSubscriptionPromotion,
  })
    .with(
      { hasSeniorityDiscount: true },
      () => "product.stickySection.freeUpfront",
    )
    .with(
      {
        hasSeniorityDiscount: P.union(false, P.nullish),
        currentVariantHasUpfrontPromotion: true,
      },
      () => "product.stickySection.upfrontPriceDifference",
    )
    .with(
      {
        hasSeniorityDiscount: P.union(false, P.nullish),
        currentVariantHasUpfrontPromotion: P.union(false, P.nullish),
        currentVariantHasSubscriptionPromotion: true,
      },
      () => "product.stickySection.freeSubscription",
    )
    .otherwise(() => null);

  const discountLabel =
    discountLabelKey &&
    discountLabelOptions &&
    i18n.t(discountLabelKey, discountLabelOptions, {
      html: true,
    });

  const CTAButton = match({
    isOutOfCatalogueWithReplacement,
    isComingSoon,
  })
    .with({ isComingSoon: true }, () => (
      <Button
        className="flex justify-center items-center w-full font-medium xs:w-max"
        onClick={openModal}
      >
        {i18n.t("product.comingSoon.bookModel", {
          productName: family.modelName,
        })}
      </Button>
    ))
    .with(
      {
        isOutOfCatalogueWithReplacement: P.not(P.union(P.nullish, false)),
      },
      () => (
        <PushableButtonLink
          className="flex justify-center items-center w-full font-medium xs:w-max"
          href={replacementModelSlug}
        >
          {i18n.t("product.stickyCommitmentDetails.order", {
            productName: family?.replacement?.modelName,
          })}
        </PushableButtonLink>
      ),
    )
    .otherwise(() => (
      <ProductOrderButton className="flex-none w-full font-medium 2xs:w-max" />
    ));

  const subscriptionId = currentVariant?.id;

  if (!subscriptionId) {
    return null;
  }

  return (
    <WithProductReminderModal
      isOpen={isOpen}
      closeModal={closeModal}
      productName={family.modelName!}
      subscriptionId={subscriptionId}
    >
      <div
        className={`${className} w-full bg-lotion z-20 shadow-[0_-4px_20px_0px_rgba(0,0,0,0.15)]`}
      >
        {/* Promotion alert */}
        {discountLabel && (
          <div className="flex justify-center items-center py-2 text-lg font-black leading-none uppercase bg-[linear-gradient(266.68deg,_#523CB5_1.46%,_#E562FF_91.41%)] text-outline-black">
            {discountLabel}
          </div>
        )}

        <div
          className={cx(
            "flex flex-col gap-2 justify-between items-center py-4 px-5 mx-auto max-w-md",
            isComingSoon || isOutOfCatalogueWithReplacement
              ? "xs:flex-row"
              : "2xs:flex-row",
          )}
        >
          {/* Price infos */}
          <div
            className={cx(
              "flex flex-row gap-y-1 justify-between items-end w-full text-sm",
              isComingSoon || isOutOfCatalogueWithReplacement
                ? "xs:flex-col xs:w-max xs:text-base"
                : "2xs:flex-col 2xs:w-max 2xs:text-base",
            )}
          >
            {isComingSoon ? (
              <>
                {/* Availability date */}
                <div className="w-full text-center">
                  <span className="mr-2 text-lg">
                    {i18n.t("product.comingSoon.availability")}
                  </span>
                  <span className="text-lg font-bold text-cornflower-blue">
                    {family.releaseDate}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div>{subscriptionPriceLabel}</div>

                <div className="flex gap-x-1">
                  {(currentVariantHasUpfrontPromotion ||
                    hasSeniorityDiscount) && (
                    <div className="hidden text-base text-[#00000080] 2xs:block 2xs:gap-x-1">
                      <s>{formattedOriginalPriceUpfront}</s>
                    </div>
                  )}
                  <div
                    className={cx("text-base font-bold", {
                      "text-[#7D89FF]":
                        currentVariantHasUpfrontPromotion ||
                        hasSeniorityDiscount,
                    })}
                  >
                    {formattedPriceUpfront}
                  </div>
                  <div>{i18n.t("product.stickySection.ofUpfront")}</div>
                </div>
              </>
            )}
          </div>

          {/* Order button */}
          {CTAButton}
        </div>
      </div>
    </WithProductReminderModal>
  );
};
